import { MapPin, Phone, ArrowUpRight, CheckCircle2, Truck, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHero } from "../components/PageHero";
import { BUSINESS, SERVICE_AREAS } from "../lib/content";

// Per-city enrichment data (kept local to the page so content.js stays simple)
const AREA_META = {
  Edmonton: {
    sub: "Alberta's Capital Region",
    population: "1.5M+",
    popLabel: "Metro Population",
    coverage: "Residential · Commercial · Municipal",
    image:
      "https://images.unsplash.com/photo-1772852340517-b11ec88979fc?fm=jpg&q=80&w=1600&auto=format&fit=crop",
    highlights: [
      "Driveway paving & reconstruction",
      "Parking lot paving & maintenance",
      "Commercial & municipal contracts",
    ],
  },
  "Grand Prairie": {
    sub: "Peace Country Hub",
    population: "70K+",
    popLabel: "Service Population",
    coverage: "Residential · Industrial · Rural",
    image:
      "https://images.unsplash.com/photo-1708117242652-25dc76c4b30c?fm=jpg&q=80&w=1600&auto=format&fit=crop",
    highlights: [
      "Rural driveways & acreage access",
      "Industrial lot paving & repairs",
      "Tar & chip seal applications",
    ],
  },
  Lloydminster: {
    sub: "The Border City · AB / SK",
    population: "32K+",
    popLabel: "City Population",
    coverage: "Residential · Commercial · Oil & Gas",
    image:
      "https://images.unsplash.com/photo-1776381733574-372689e246b4?fm=jpg&q=80&w=1600&auto=format&fit=crop",
    highlights: [
      "Oil-sector site paving",
      "Commercial parking lots",
      "Seal coating & crack sealing",
    ],
  },
};

export default function ServiceArea() {
  return (
    <div data-testid="service-area-page">
      <PageHero
        eyebrow="Service Area"
        title="Serving"
        accent="the entire North."
        subtitle="From Edmonton to the Peace Country to the Border City — three regional hubs covering Western Canada. For larger projects, we travel beyond our core area."
        testId="service-area-hero"
      />

      {/* Coverage summary band */}
      <section
        className="py-16 bg-[#050505] border-b border-[#27272a]"
        data-testid="service-area-summary"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-0 md:divide-x md:divide-[#27272a]">
          <div className="md:px-10 first:md:pl-0">
            <MapPin className="w-6 h-6 text-[#f59e0b] mb-4" />
            <div className="font-display font-black text-5xl md:text-6xl text-[#fafaf9] leading-none mb-3">
              3
            </div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#a1a1aa]">
              Regional Hubs
            </div>
          </div>
          <div className="md:px-10">
            <Truck className="w-6 h-6 text-[#f59e0b] mb-4" />
            <div className="font-display font-black text-5xl md:text-6xl text-[#fafaf9] leading-none mb-3">
              500<span className="text-[#f59e0b]">km+</span>
            </div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#a1a1aa]">
              Travel Radius
            </div>
          </div>
          <div className="md:px-10">
            <Users className="w-6 h-6 text-[#f59e0b] mb-4" />
            <div className="font-display font-black text-5xl md:text-6xl text-[#fafaf9] leading-none mb-3">
              1.6<span className="text-[#f59e0b]">M+</span>
            </div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#a1a1aa]">
              People Served
            </div>
          </div>
          <div className="md:px-10 last:md:pr-0">
            <CheckCircle2 className="w-6 h-6 text-[#f59e0b] mb-4" />
            <div className="font-display font-black text-5xl md:text-6xl text-[#fafaf9] leading-none mb-3">
              40<span className="text-[#f59e0b]">+</span>
            </div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#a1a1aa]">
              Years In Region
            </div>
          </div>
        </div>
      </section>

      {/* Regional cards */}
      <section className="py-24 md:py-32" data-testid="service-area-regions">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex items-end justify-between mb-14 gap-6 flex-wrap">
            <div>
              <div className="label-sm text-[#f59e0b] mb-5">Our regions</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9]">
                Three hubs.
                <br />
                <span className="text-[#f59e0b]">One promise.</span>
              </h2>
            </div>
            <p className="text-[#a1a1aa] max-w-md">
              Each regional office serves residential, commercial, industrial
              and municipal clients with the same disciplined paving standard.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {SERVICE_AREAS.map((a, i) => {
              const meta = AREA_META[a.city] || {};
              return (
                <div
                  key={i}
                  className="group relative bg-[#18181b] border border-[#27272a] hover:border-[#f59e0b] transition-all duration-500 overflow-hidden flex flex-col"
                  data-testid={`area-card-${i}`}
                >
                  {/* Image header */}
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={meta.image}
                      alt={`${a.city} paving`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/30 to-transparent" />
                    <div className="absolute top-4 left-4 w-10 h-10 bg-[#f59e0b] text-[#09090b] flex items-center justify-center font-display font-black text-sm">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="absolute bottom-4 left-5 right-5">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#f59e0b] font-bold mb-2">
                        {meta.sub}
                      </div>
                      <div className="font-display font-black text-4xl md:text-5xl uppercase leading-none text-[#fafaf9]">
                        {a.city}
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-7 flex-1 flex flex-col">
                    {/* Region + population row */}
                    <div className="flex items-start justify-between gap-4 pb-5 border-b border-[#27272a]">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#a1a1aa] mb-1">
                          Region
                        </div>
                        <div className="font-bold text-[#fafaf9]">
                          {a.region}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#a1a1aa] mb-1">
                          {meta.popLabel}
                        </div>
                        <div className="font-display font-black text-[#f59e0b] text-xl">
                          {meta.population}
                        </div>
                      </div>
                    </div>

                    {/* Note */}
                    <p className="text-[#d4d4d8] text-sm leading-relaxed my-5">
                      {a.note}
                    </p>

                    {/* Highlights */}
                    <div className="mt-auto">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#f59e0b] font-bold mb-4">
                        What we cover
                      </div>
                      <ul className="space-y-2.5 mb-6">
                        {meta.highlights?.map((h, j) => (
                          <li
                            key={j}
                            className="flex items-start gap-3 text-sm text-[#fafaf9]"
                          >
                            <CheckCircle2
                              className="w-4 h-4 text-[#f59e0b] mt-0.5 shrink-0"
                              strokeWidth={2.5}
                            />
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="flex items-center gap-3 pt-4 border-t border-[#27272a] text-[10px] uppercase tracking-[0.3em] text-[#52525b]">
                        <MapPin className="w-3.5 h-3.5 text-[#f59e0b]" />
                        <span>{meta.coverage}</span>
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <a
                    href={`tel:${BUSINESS.phoneRaw}`}
                    data-testid={`area-card-${i}-call`}
                    className="flex items-center justify-between px-7 py-5 border-t border-[#27272a] group-hover:border-[#f59e0b]/40 bg-[#09090b] group-hover:bg-[#f59e0b] transition-colors"
                  >
                    <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#fafaf9] group-hover:text-[#09090b]">
                      Request a quote
                    </span>
                    <ArrowUpRight
                      className="w-4 h-4 text-[#f59e0b] group-hover:text-[#09090b] transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                      strokeWidth={3}
                    />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Beyond + CTA band */}
      <section
        className="py-24 md:py-28 bg-[#050505] border-y border-[#27272a]"
        data-testid="service-area-beyond"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="label-sm text-[#f59e0b] mb-5">Beyond our regions</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9] mb-6">
              Larger projects?
              <br />
              <span className="text-[#f59e0b]">We travel.</span>
            </h2>
            <p className="text-[#a1a1aa] text-lg leading-relaxed max-w-xl">
              North West Asphalt extends services throughout Western Canada for
              larger commercial, industrial and municipal paving contracts.
              Give us the project details and we'll put together a plan.
            </p>
          </div>
          <div
            className="bg-[#18181b] border border-[#27272a] p-8 md:p-10 relative overflow-hidden"
            data-testid="area-contact-card"
          >
            <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full bg-[#f59e0b]/10 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="label-sm text-[#f59e0b] mb-5">Ready to start?</div>
              <div className="font-display font-black text-3xl md:text-4xl uppercase mb-6 leading-tight">
                Call or email for a<br />
                site visit & written quote.
              </div>
              <div className="space-y-3 mb-8 text-[#fafaf9]">
                <a
                  href={`tel:${BUSINESS.phoneRaw}`}
                  className="flex items-center gap-3 hover:text-[#f59e0b] transition-colors text-lg"
                  data-testid="area-phone-link"
                >
                  <Phone className="w-5 h-5 text-[#f59e0b]" strokeWidth={3} />
                  <span className="font-bold">{BUSINESS.phone}</span>
                </a>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={`tel:${BUSINESS.phoneRaw}`}
                  className="inline-flex items-center gap-3 bg-[#f59e0b] hover:bg-[#d97706] text-[#09090b] font-bold px-7 py-4 text-xs uppercase tracking-[0.25em] transition-colors"
                  data-testid="area-call-btn"
                >
                  <Phone className="w-4 h-4" strokeWidth={3} />
                  Call us
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 border border-[#3f3f46] hover:border-[#f59e0b] text-[#fafaf9] font-bold px-7 py-4 text-xs uppercase tracking-[0.25em] transition-colors"
                  data-testid="area-contact-cta"
                >
                  Contact page
                  <ArrowUpRight className="w-4 h-4" strokeWidth={3} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
